import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

type Mode = 'signin' | 'signup' | 'forgot'

/**
 * Email and password.
 *
 * Magic links were the original design and they lose on one specific thing:
 * on iOS a link tapped in Mail opens Safari, not the installed app. Someone
 * who adds Gridlock to their home screen and then signs in from email ends up
 * authenticated in Safari while the icon still shows them logged out. That is
 * exactly the person we most need not to lose in week one.
 *
 * A password gets typed into whichever app you are standing in, so the
 * home-screen install and the sign-in are the same session.
 *
 * Email is still a dependency — a forgotten password is reset by email — but
 * it is no longer on the critical path for playing.
 */
export default function Login() {
  const [mode, setMode] = useState<Mode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [note, setNote] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const nav = useNavigate()
  const { session } = useAuth()

  // Navigating during render is a React no-no; it also fired on every keystroke.
  useEffect(() => {
    if (session) nav('/app', { replace: true })
  }, [session, nav])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    setNote(null)

    if (mode === 'forgot') {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset`,
      })
      setBusy(false)
      if (error) return setErr(error.message)
      // Deliberately not "we found your account" — that would let anyone test
      // which addresses have signed up.
      return setNote(`If ${email.trim()} has an account, a reset link is on its way.`)
    }

    if (mode === 'signup') {
      if (password.length < 8) {
        setBusy(false)
        return setErr('Use at least 8 characters.')
      }
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: { display_name: displayName.trim() || email.trim().split('@')[0] },
        },
      })
      setBusy(false)
      if (error) return setErr(error.message)
      // If confirmations are on, there is no session yet and the user needs to
      // go find an email. Say so rather than dropping them on a blank screen.
      if (!data.session) {
        return setNote('Account created. Check your email to confirm, then sign in.')
      }
      return nav('/app', { replace: true })
    }

    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    })
    setBusy(false)
    if (error) {
      return setErr(
        error.message.toLowerCase().includes('invalid login')
          ? 'That email and password do not match.'
          : error.message,
      )
    }
    nav('/app', { replace: true })
  }

  const swap = (m: Mode) => {
    setMode(m)
    setErr(null)
    setNote(null)
  }

  return (
    <div className="center">
      <div className="card auth-card">
        <Link className="wordmark" to="/">GRIDLOCK</Link>

        <form onSubmit={submit}>
          <h2>
            {mode === 'signin' && 'Sign in'}
            {mode === 'signup' && 'Create account'}
            {mode === 'forgot' && 'Reset password'}
          </h2>

          <p className="muted">
            {mode === 'signin' && 'Draft the room, not the guy.'}
            {mode === 'signup' && 'Takes about twenty seconds.'}
            {mode === 'forgot' && "We'll email you a link to set a new one."}
          </p>

          {mode === 'signup' && (
            <label>
              Your name
              <input
                autoComplete="name"
                placeholder="Josh"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </label>
          )}

          <label>
            Email
            <input
              type="email"
              required
              autoComplete="email"
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          {mode !== 'forgot' && (
            <label>
              Password
              <input
                type="password"
                required
                minLength={mode === 'signup' ? 8 : undefined}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                placeholder={mode === 'signup' ? 'At least 8 characters' : ''}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
          )}

          <button className="btn btn-primary full" disabled={busy}>
            {busy
              ? 'Working…'
              : mode === 'signin'
                ? 'Sign in'
                : mode === 'signup'
                  ? 'Create account'
                  : 'Send reset link'}
          </button>

          {err && <p className="error">{err}</p>}
          {note && <p className="ok">{note}</p>}

          <div className="auth-switch">
            {mode === 'signin' && (
              <>
                <button type="button" className="linkish" onClick={() => swap('signup')}>
                  Create an account
                </button>
                <button type="button" className="linkish" onClick={() => swap('forgot')}>
                  Forgot password
                </button>
              </>
            )}
            {mode !== 'signin' && (
              <button type="button" className="linkish" onClick={() => swap('signin')}>
                Back to sign in
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}
